import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Divider, Drawer } from "@mui/material";
import { AlertTabType } from "@/store/alerts/utils/types/types";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { alertsActiveTabSelector } from "@/store/alerts/selectors/alertsActiveTabSelector";
import { alertsOpenDrawerSelector } from "@/store/alerts/selectors/alertsOpenDrawerSelector";
import { resetCurrentBudget } from "@/store/budgets/budgetsSlice";
import { BudgetsDrawer } from "@/components/pages/budgets-and-alerts/components/drawer/budget-alert-create-edit-drawer/components/budgets/BudgetsDrawer";
import { AlertsUtilizationDrawer } from "@/components/pages/budgets-and-alerts/components/drawer/budget-alert-create-edit-drawer/components/utilization/AlertsUtilizationDrawer";
import { AlertsCostsAnomalyDrawer } from "@/components/pages/budgets-and-alerts/components/drawer/budget-alert-create-edit-drawer/components/cost-anomaly/AlertsCostAnomalyDrawer";
import { AlertDrawerHeader } from "@/components/pages/budgets-and-alerts/components/drawer/budget-alert-create-edit-drawer/components/common/AlertsDrawerHeader";
import { usePanelHorizontalResize } from "@/components/utils/hooks/usePanelHorizontalResize";
import { ResourceDetailsDragHandler } from "@/components/pages/common/resource-details/components/ResourceDetailsDragHandler";
import {
  resetAlertsData,
  toggleAlertsOpenDrawer,
} from "@/store/alerts/alertsSlice";

export const BudgetsAndAlertsEditorDrawer: FC = () => {
  const dispatch = useAppDispatch();
  const alertsActiveTab = useAppSelector(alertsActiveTabSelector);
  const alertsOpenDrawer = useAppSelector(alertsOpenDrawerSelector);
  const [drawerTab, setDrawerTab] = useState<AlertTabType>(alertsActiveTab);

  const { width: alertsWidth, handleMouseDown: alertsHandleMouseDown } =
    usePanelHorizontalResize(alertsResizingDefaultParams);

  const { width: budgetsWidth, handleMouseDown: budgetsHandleMouseDown } =
    usePanelHorizontalResize(budgetResizingDefaultParams);

  const handleMouseDown = useMemo(() => {
    return alertsActiveTab === "budget"
      ? budgetsHandleMouseDown
      : alertsHandleMouseDown;
  }, [alertsActiveTab, budgetsHandleMouseDown, alertsHandleMouseDown]);

  const handleClose = useCallback(() => {
    dispatch(toggleAlertsOpenDrawer());
    setDrawerTab(alertsActiveTab);

    if (drawerTab === "budget") {
      dispatch(resetCurrentBudget());
    } else {
      dispatch(resetAlertsData());
    }
  }, [alertsActiveTab, drawerTab, dispatch]);

  useEffect(() => {
    setDrawerTab(alertsActiveTab);
  }, [alertsActiveTab]);

  return (
    <Drawer
      anchor="right"
      open={alertsOpenDrawer}
      onClose={handleClose}
      slotProps={slotProps}
      PaperProps={{
        sx: {
          overflow: "hidden",
          width: alertsActiveTab === "budget" ? budgetsWidth : alertsWidth,
        },
      }}
    >
      <AlertDrawerHeader onClose={handleClose} />
      <Divider />

      <Fragment>
        {drawerTab === "budget" && <BudgetsDrawer />}
        {drawerTab === "costAnomaly" && (
          <AlertsCostsAnomalyDrawer onClose={handleClose} />
        )}
        {drawerTab === "utilization" && (
          <AlertsUtilizationDrawer onClose={handleClose} />
        )}
      </Fragment>

      <ResourceDetailsDragHandler onMouseDown={handleMouseDown} />
    </Drawer>
  );
};

const slotProps = {
  backdrop: {
    invisible: true,
  },
};

const budgetResizingDefaultParams = {
  defaultWidthPercent: 80,
  minWidthPercent: 45,
  key: "budgetsDrawer",
};

const alertsResizingDefaultParams = {
  defaultWidthPercent: 50,
  minWidthPercent: 30,
  key: "alertsDrawer",
};
